import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { orderService } from '../../../shared/services/orderService';
import { useShop } from '../../../shared/store';

interface PendingCheckoutPaymentSession {
  paymentReference?: string;
}

const PENDING_PAYMENT_STORAGE_KEY = 'haapa.checkout.pendingPayment';

const readPendingPayment = (): PendingCheckoutPaymentSession | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = window.sessionStorage.getItem(PENDING_PAYMENT_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as PendingCheckoutPaymentSession;
  } catch {
    return null;
  }
};

const persistPendingPayment = (session: PendingCheckoutPaymentSession | null) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!session) {
    window.sessionStorage.removeItem(PENDING_PAYMENT_STORAGE_KEY);
    return;
  }

  window.sessionStorage.setItem(PENDING_PAYMENT_STORAGE_KEY, JSON.stringify(session));
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const asBoolean = (value: unknown): boolean | undefined => (typeof value === 'boolean' ? value : undefined);

const normalizeStatus = (value: string | null): 'successful' | 'failed' | 'pending' => {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) {
    return 'pending';
  }

  if (['successful', 'success', 'completed', 'paid'].includes(normalized)) {
    return 'successful';
  }

  if (['failed', 'cancelled', 'canceled', 'error', 'declined'].includes(normalized)) {
    return 'failed';
  }

  return 'pending';
};

const CheckoutPaymentCallbackPage = () => {
  const navigate = useNavigate();
  const { refreshOrders, refreshCart } = useShop();
  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState('Finalizing your payment...');
  const [isError, setIsError] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const didProcessRef = useRef(false);

  useEffect(() => {
    if (didProcessRef.current) {
      return;
    }
    didProcessRef.current = true;

    let isMounted = true;

    const finalizePayment = async () => {
      const pendingSession = readPendingPayment();
      const referenceFromQuery =
        searchParams.get('tx_ref') ||
        searchParams.get('reference') ||
        searchParams.get('txRef');
      const paymentReference = referenceFromQuery || pendingSession?.paymentReference;
      const status = normalizeStatus(searchParams.get('status'));
      const transactionId =
        searchParams.get('transaction_id') ||
        searchParams.get('transactionId') ||
        searchParams.get('transaction') ||
        undefined;

      if (!paymentReference) {
        if (!isMounted) {
          return;
        }
        setIsError(true);
        setIsComplete(true);
        setMessage('Payment reference is missing. Please restart checkout payment.');
        return;
      }

      try {
        const result = await orderService.confirmCheckout(paymentReference, transactionId ?? undefined);
        await refreshOrders();
        await refreshCart();

        if (!isMounted) {
          return;
        }

        const record = asRecord(result);
        const dataRecord = asRecord(record.data);
        const verified =
          asBoolean(dataRecord.verified) ??
          asBoolean(record.verified) ??
          asBoolean(asRecord(dataRecord.data).verified);

        if (verified === true) {
          persistPendingPayment(null);
          setIsError(false);
          setMessage('Payment confirmed successfully.');
          setIsComplete(true);
          setShowSuccessPopup(true);
          return;
        }

        setIsError(status === 'failed' || verified === false);
        setIsComplete(true);
        setMessage(
          status === 'failed' || verified === false
            ? 'Payment was not completed. You can try again.'
            : 'Payment is still pending. You can retry after a moment.',
        );
      } catch (error: unknown) {
        if (!isMounted) {
          return;
        }
        const err = error as { message?: string };
        setIsError(true);
        setIsComplete(true);
        setMessage(err.message || 'Unable to confirm payment right now. Please try again.');
      }
    };

    void finalizePayment();

    return () => {
      isMounted = false;
    };
  }, [navigate, refreshOrders, searchParams]);

  return (
    <section className="mx-auto max-w-xl animate-fade-up space-y-5 rounded-3xl border border-orange-100 bg-white p-8 text-center shadow-lg">
      <h2 className="text-2xl font-semibold text-orange-700">Flutterwave Payment Callback</h2>
      <p className={`text-sm ${isError ? 'font-semibold text-red-600' : 'text-slate-600'}`}>{message}</p>

      {isComplete && !showSuccessPopup && (
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/checkout/payment"
            className="rounded-xl border border-orange-300 px-4 py-2 text-sm font-semibold text-orange-700 transition hover:bg-orange-50"
          >
            Back to Payment
          </Link>
          <Link
            to="/checkout"
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Back to Checkout
          </Link>
        </div>
      )}

      {showSuccessPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-emerald-700">Paid Successfully</h3>
            <p className="mt-2 text-sm text-slate-600">
              Your payment was confirmed. You can now track your order and view it in your dashboard.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowSuccessPopup(false);
                  void navigate('/dashboard/tracking', { replace: true });
                }}
                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
              >
                Go to Tracking
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowSuccessPopup(false);
                  void navigate('/dashboard/transactions', { replace: true });
                }}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                View Transactions
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CheckoutPaymentCallbackPage;
